import { BackToTop } from 'components/common';
import { ComposableFilters } from 'components/common/ComposableFilters';
import { SaveSegment } from 'components/common/ComposableFilters/SaveSegment';
import { SegmentSelector } from 'components/common/SegmentSelector';
import { ExternalLinkIcon } from 'components/icons';
import SaveIcon from 'components/icons/SaveIcon';
import {
  ListOfCustomers,
  MostPurchasedProducts,
  Overview,
} from 'components/segments';
import { CustomerAttributes } from 'components/segments/CustomerAttributes';
import ExportUsers from 'components/segments/ExportUsers';
import { SuggestionCallout } from 'components/ui';
import { isEmpty } from 'lodash';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetCustomerSegmentsQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';

export default function CreateNewSegment() {
  usePageTitle('Create new segment | Segments');

  const {
    output: { filtersApiPayload },
    applied,
  } = useAppSelector(composableFiltersState);

  const { currentStore } = useAppSelector(userDetailsState);

  const { isFetching: isFetchingSegments } = useGetCustomerSegmentsQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const isNewSegment = !!!isEmpty(filtersApiPayload) && !!!applied.segment;

  return (
    <div className="max-w-screen-xl px-5 mx-auto md:pl-segments-72px md:pr-20 py-11">
      <p className="font-bold leading-6">Segments</p>
      <h1 className="mt-4 font-bold leading-10 text-segments-44px">
        Create new segment
      </h1>
      <p className="mt-6 text-base">
        Select a segment or filters for a more specific list. You can save new
        filter combinations, and favorite any segment to have them appear first.
        Also, you can export the segment details in CSV, FB Audience, Google
        Audience, and addresses by clicking the drop down menu of the export
        button on this page.
      </p>
      <p className="mt-2">
        We only include valid orders because we exclude orders that are
        refunded, invalid, or draft status from the calculations below.
      </p>
      <a
        href="https://help.tresl.co/en/articles/5401160-how-to-create-new-segments"
        className="inline-flex items-start space-x-0.5 text-segments-link mt-2 font-bold"
        target="_blank"
        rel="noreferrer"
      >
        <p>Learn more</p>
        <ExternalLinkIcon className="inline w-5 h-5 align-middle" />
      </a>

      <div className="flex items-center justify-between w-full py-3 pl-5 pr-10 text-base border bg-segments-accent-green-3 rounded-2xl mt-7">
        <div className="flex items-center space-x-2">
          <SegmentSelector />
          {isNewSegment && !!!isFetchingSegments && (
            <SaveSegment>
              <div className="flex items-center space-x-2">
                <SaveIcon className="w-5 h-5 text-gray-800 hover:text-black" />
                <p className="font-bold">Save segment</p>
              </div>
            </SaveSegment>
          )}
        </div>
        <ComposableFilters />
      </div>

      <div className="mt-4 md:mt-10">
        <SuggestionCallout>
          <div className="px-6 py-4">
            <p className="text-black text-opacity-60">Segment description</p>
            <p className="mt-2">
              {isFetchingSegments
                ? 'Loading'
                : applied.segment?.description
                ? applied.segment.description
                : isNewSegment
                ? 'Save segment to add a description'
                : 'Select a segment or filter'}
            </p>
            {applied.segment?.segment_name && (
              <p className="mt-2 text-black text-opacity-60 text-segments-13px">
                Synced audience / tag name: [segments -{' '}
                {currentStore.completeInfo?.store_name}]{' '}
                {applied.segment?.segment_name}
              </p>
            )}
          </div>
        </SuggestionCallout>
      </div>
      <Overview />
      <div className="flex justify-end mt-8 ">
        <ExportUsers />
      </div>
      <CustomerAttributes />
      <MostPurchasedProducts />
      <ListOfCustomers />
      <BackToTop />
    </div>
  );
}
